import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const ORG = '#bf1e2e';
const RED = '#bf1e2e';

type Step = 'code' | 'stage' | 'register' | 'confirm';

const EP_STAGES = [
  {
    id: 1,
    name: 'Foundation and Fundamentals',
    ages: '8 and under',
    description: 'Athletes begin building identity vocabulary and character habits alongside their first competitive experience.',
  },
  {
    id: 2,
    name: 'Discovery',
    ages: '9 to 11',
    description: 'Self awareness deepens. Athletes begin to understand who they are beyond the court and why it matters.',
  },
  {
    id: 3,
    name: 'Profile and Branding',
    ages: '12 to 13',
    description: 'Athletes take independent ownership of their profile and NIL awareness.',
  },
  {
    id: 4,
    name: 'NIL Awareness and Readiness',
    ages: '14 to 17',
    description: 'By Varsity age, the habits, character, and profile are already in place.',
  },
];

interface Institution {
  id: string;
  name: string;
  type: string;
  max_athletes: number | null;
}

export default function EarlyPathwaysSignupPage() {
  const [step, setStep] = useState<Step>('code');
  const [orgCode, setOrgCode] = useState('');
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [selectedStage, setSelectedStage] = useState<number | null>(null);
  const [athleteName, setAthleteName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const code = orgCode.trim().toUpperCase();
    const { data: inst, error: instErr } = await supabase
      .from('institutions')
      .select('id, name, type, max_athletes')
      .eq('org_code', code)
      .single();

    if (instErr || !inst) {
      setError('Organization code not found. Check with your program coordinator.');
      setLoading(false);
      return;
    }

    if (inst.max_athletes !== null) {
      const { count } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('institution_id', inst.id)
        .eq('user_type', 'early_pathways');

      if ((count ?? 0) >= inst.max_athletes) {
        setError('This program has reached its enrollment limit. Contact your coordinator.');
        setLoading(false);
        return;
      }
    }

    setInstitution(inst);
    setStep('stage');
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!institution || selectedStage === null) return;
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }

    setLoading(true);
    setError('');

    const { data: authData, error: signUpErr } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + '/early-pathways/login',
        data: {
          full_name: athleteName.trim(),
          user_type: 'early_pathways',
          institution_id: institution.id,
          institution_type: institution.type,
          ep_stage: selectedStage,
        },
      },
    });

    if (signUpErr || !authData.user) {
      setError(signUpErr?.message ?? 'Account creation failed. Try again.');
      setLoading(false);
      return;
    }

    // Profile and ep_progress are created by the handle_new_user trigger
    // using the metadata above — no client-side upsert needed.
    setStep('confirm');
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px', borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  const BthdMark = (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      background: 'rgba(191,30,46,0.08)', border: `1px solid ${RED}`,
      borderRadius: 30, padding: '6px 20px', marginBottom: 16,
    }}>
      <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 13, color: RED, letterSpacing: 2 }}>EARLY PATHWAYS</span>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0f1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Barlow, sans-serif', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: step === 'stage' ? 560 : 420 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="Bigger Than Hoop Dreams"
            style={{ height: 36, width: 'auto', filter: 'brightness(0) invert(1)', marginBottom: 20 }}
          />
          <br />
          {BthdMark}
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 22, color: '#fff', letterSpacing: 1 }}>
            {step === 'code' && 'CREATE YOUR ACCOUNT'}
            {step === 'stage' && 'SELECT YOUR STAGE'}
            {step === 'register' && 'COMPLETE REGISTRATION'}
            {step === 'confirm' && 'CHECK YOUR EMAIL'}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            {step === 'code' && 'Enter your organization code to get started'}
            {step === 'stage' && `${institution?.name}, select the stage that matches the athlete's age`}
            {step === 'register' && `Signing up through ${institution?.name}`}
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 28 }}>
          {(['code', 'stage', 'register'] as Step[]).map((s, i) => (
            <React.Fragment key={s}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: (step !== 'confirm' && step === s) ? ORG :
                  (step === 'confirm' || i < ['code', 'stage', 'register'].indexOf(step)) ? 'rgba(191,30,46,0.4)' : 'rgba(255,255,255,0.1)',
              }} />
              {i < 2 && <div style={{ width: 24, height: 1, background: 'rgba(255,255,255,0.1)' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 — Org Code */}
        {step === 'code' && (
          <form onSubmit={validateCode}>
            <div style={{ marginBottom: 14 }}>
              <input
                type="text"
                placeholder="Organization code"
                value={orgCode}
                onChange={e => setOrgCode(e.target.value)}
                required
                style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: 2 }}
              />
            </div>
            {error && <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 14, textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '14px', borderRadius: 10,
              background: ORG, border: 'none', color: '#fff',
              fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: 1,
              cursor: 'pointer', opacity: loading ? 0.7 : 1,
            }}>
              {loading ? 'Checking…' : 'CONTINUE'}
            </button>
          </form>
        )}

        {/* Step 2 — Stage Selection */}
        {step === 'stage' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {EP_STAGES.map(s => {
                const active = selectedStage === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedStage(s.id)}
                    style={{
                      textAlign: 'left', padding: '16px 18px', borderRadius: 12, cursor: 'pointer',
                      background: active ? 'rgba(191,30,46,0.1)' : 'rgba(255,255,255,0.03)',
                      border: active ? `1.5px solid ${ORG}` : '1.5px solid rgba(255,255,255,0.07)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                          <span style={{
                            fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                            fontSize: 10, letterSpacing: 2,
                            color: active ? ORG : 'rgba(255,255,255,0.4)',
                            background: active ? 'rgba(191,30,46,0.15)' : 'rgba(255,255,255,0.06)',
                            padding: '2px 7px', borderRadius: 4,
                          }}>STAGE {s.id}</span>
                          <span style={{
                            fontSize: 11, color: active ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
                            fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 1,
                          }}>
                            AGES {s.ages.toUpperCase()}
                          </span>
                        </div>
                        <div style={{
                          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                          fontSize: 16, color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                          marginBottom: 5,
                        }}>
                          {s.name}
                        </div>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
                          {s.description}
                        </div>
                      </div>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                        border: active ? `2px solid ${ORG}` : '2px solid rgba(255,255,255,0.15)',
                        background: active ? ORG : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {error && <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 14, textAlign: 'center' }}>{error}</div>}

            <button
              onClick={() => {
                if (!selectedStage) { setError('Please select a stage to continue.'); return; }
                setError('');
                setStep('register');
              }}
              style={{
                width: '100%', padding: '14px', borderRadius: 10,
                background: selectedStage ? ORG : 'rgba(255,255,255,0.08)',
                border: 'none', color: selectedStage ? '#fff' : 'rgba(255,255,255,0.3)',
                fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: 1,
                cursor: selectedStage ? 'pointer' : 'default',
              }}
            >
              CONTINUE
            </button>
            <button
              type="button"
              onClick={() => { setStep('code'); setError(''); setSelectedStage(null); }}
              style={{ width: '100%', marginTop: 10, padding: '10px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 13, cursor: 'pointer' }}
            >
              ← Change organization code
            </button>
          </div>
        )}

        {/* Step 3 — Register */}
        {step === 'register' && (
          <form onSubmit={handleRegister}>
            <div style={{
              background: 'rgba(191,30,46,0.08)', border: '1px solid rgba(191,30,46,0.2)',
              borderRadius: 10, padding: '12px 16px', marginBottom: 20,
              fontSize: 13, color: 'rgba(255,255,255,0.7)',
            }}>
              <strong style={{ color: '#fff' }}>{institution?.name}</strong>
              {' · '}
              <span style={{ color: ORG }}>
                Stage {selectedStage}: {EP_STAGES.find(s => s.id === selectedStage)?.name}
              </span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <input type="text" placeholder="Athlete's full name" value={athleteName}
                onChange={e => setAthleteName(e.target.value)} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <input type="email" placeholder="Parent, guardian or athlete email" value={email}
                onChange={e => setEmail(e.target.value)} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <input type="password" placeholder="Create a password (8+ characters)" value={password}
                onChange={e => setPassword(e.target.value)} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <input type="password" placeholder="Confirm password" value={confirm}
                onChange={e => setConfirm(e.target.value)} required style={inputStyle} />
            </div>

            {error && <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 14, textAlign: 'center' }}>{error}</div>}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '14px', borderRadius: 10,
              background: ORG, border: 'none', color: '#fff',
              fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: 1,
              cursor: 'pointer', opacity: loading ? 0.7 : 1,
            }}>
              {loading ? 'Creating account…' : 'CREATE ACCOUNT'}
            </button>
            <button type="button" onClick={() => { setStep('stage'); setError(''); }} style={{
              width: '100%', marginTop: 10, padding: '10px', background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.3)', fontSize: 13, cursor: 'pointer',
            }}>
              ← Back to stage selection
            </button>
          </form>
        )}

        {/* Step 4 — Email confirmation */}
        {step === 'confirm' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 38, marginBottom: 16 }}>✉️</div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: 28 }}>
              We sent a confirmation link to{' '}
              <strong style={{ color: '#fff' }}>{email}</strong>.
              Click the link in that email to activate your account, then sign in.
            </p>
            <a href="/early-pathways/login" style={{
              display: 'block', width: '100%', padding: '14px', borderRadius: 10,
              background: ORG, color: '#fff', textDecoration: 'none', textAlign: 'center',
              fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: 1,
            }}>
              GO TO SIGN IN
            </a>
          </div>
        )}

        {step !== 'confirm' && (
          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <a href="/early-pathways/login" style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }}>
              Already have an account? Sign in
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
